import type {
  Address,
  Custom,
  MerchandisingSettings,
  Metafields,
  MetafieldSchema,
} from 'types/general';
import type { ShippingSettings } from 'types/models/shipping';

export enum CommerceType {
  None = '',
  Buyer = 'buyer',
  Seller = 'seller',
}

export enum Platform {
  Shopify = 'shopify',
  WooCommerce = 'woocommerce',
  BigCommerce = 'bigcommerce',
  Magento2 = 'magento2',
  CsvSftp = 'csv-sftp',
  Other = 'other',
  ExternalSftp = 'external-sftp',
  EdiSftp = 'edi-sftp',
  EdiExternalSftp = 'edi-sftp-external',
}

export type CompanyActiveAuth = Record<
  string,
  | CompanyShopifySettings
  | CompanyMagento2Auth
  | CompanyWooCommerceAuth
  | CompanyBigCommerceAuth
  | CompanyExternalSFTPAuth
>;

export type CompanyTerms = {
  provider: string;
  providerId: string;
  approved: boolean;
  approvedDate: string;
  denied: boolean;
  deniedDate: string;
  amountAvailable: number;
  amountAuthorized: number;
  started: boolean;
  startedDate: string;
  updatedDate: string;
  applied: boolean;
  appliedDate: string;
  applicationId: string;
};

export type CompanyDefaults = {
  priceDiscount: number;
  priceListID: string;
};

export type CompanySFTPSettings = {
  downloadDirectory: string;
  uploadDirectory: string;
  archive: { method: string; directory: string };
};

export type CompanyEDIData = {
  isaID: string;
  isaIDQualifier: string;
  counters: {
    interchangeCtrlNum: number;
    functionalGroupNumber: number;
    orderNum: number;
    invoiceNum: number;
  };
  postFulfillment: string;
  warehouseMetafieldNamespace: string;
  warehouseDefaultMetafieldKey: string;
  acceptedFileExtensions: string[];
  errors: any[]; // TODO
  characterEncoding: string;
};

export type CompanyPrivacy = {
  disableSudoAccess: boolean;
};

export type CompanySyncing = {
  postInvoicesDate: string;
  getProductsStart: string;
  getProductsEnd: string;
  getEDIAcknowledgements: boolean;
  postEDIAcknowledgements: boolean;
  postEDIInventoryUpdate: boolean;
  postEDIInvoices: boolean;
  getEDIInvoices: boolean;
  getEDIInventoryUpdates: boolean;
  getOrders: boolean;
  postOrders: boolean;
  lastEDIInventoryUpdate: string;
};

export type CompanyGettingStarted = {
  allStepsCompleted: boolean;
  allStepsCompletedAt: string;
  inviteTeamCompleted: string;
  invitePartnerCompleted: string;
  sftpFileUploadCompleted: boolean;
  ediSpecsReviewCompleted: boolean;
  testOrderCompleted: boolean;
};

export type CompanyNotifications = {
  ordersEmail: string;
  ordersNotify: boolean;
};

export type CompanyWebhook = {
  ID: number;
  Key: string;
};

export type CompanyFlags = {
  skipBuyerNotifications: boolean;
  ignoreInventoryType: boolean;
  acceptPartialOrders: boolean;
  notifyCriticalErrors: boolean;
  syncUnpublishedProducts: boolean;
  mergeAllTags: boolean;
  useNewCSVProductWorker: boolean;
  overwriteComparedAtPrice: boolean;
  useRetailPriceForComparedAt: boolean;
  pushProductsAsTaxable: boolean;
  balanceEnabled: boolean;
};

export type CompanyPlan = {
  chargeOrderFee: boolean;
  orderFee: number;
};

export type CompanyUser = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
};

export interface CompanyReturnsSettings {
  syncToPlatform: boolean;
  shouldRefundOnReturnComplete: boolean;
}

interface CompanyPaymentTerms {
  numBillingDelayDays: number;
}

export type CompanyEasyPostSettings = {
  connected: boolean;
};

export type BalanceQualificationStatus =
  | 'pending'
  | 'pendingActivation'
  | 'approved'
  | 'declined'
  | 'inReview'
  | '';

export interface CompanyBalanceQualification {
  status?: BalanceQualificationStatus;
  link?: string;
  reason?: string;
  updatedAt?: string;
}

export interface CompanyBalance {
  buyerId?: string;
  paymentMethodId?: string;
  paymentMethodType?: string;
  paymentMethodVerified?: boolean;
  vendorId?: string;
  qualification?: CompanyBalanceQualification;
}

export interface CompanyDefaultBillingSettings {
  generateOrderInvoices?: boolean;
  autoTax?: boolean;
  postInvoices?: boolean;
  paymentTerms: CompanyPaymentTerms;
}

export interface CompanyDomain {
  url: string;
  isVerified: boolean;
}

export interface CompanyShopifySettings {
  platform: Platform;
  store: string;
  permissions: string;
  productCollectionId?: string;
  shareCustomerEmailAddress?: boolean;
  shareCustomerPhoneNumber?: boolean;
  locationIds?: number[];
}

export interface CompanyMagento2Auth {
  platform: Platform;
  platformUrl: string;
  secretID: string;
  storeView: string;
}

export interface CompanyWooCommerceAuth {
  platform: Platform;
  platformUrl: string;
}

export interface CompanyBigCommerceAuth {
  platform: Platform;
  platformUrl: string;
  bigCommerceClientID: string;
}

export interface CompanyExternalSFTPAuth {
  platform: Platform;
  username: string;
  address: string;
  port: number;
  secretID: string;
}

export interface CompanyDataExports {
  active: boolean;
  externalSFTP: CompanyExternalSFTPAuth;
}

export type MarketplaceContact = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  profile?: string;
  jobTitle?: string;
};

export enum AttributeKey {
  MADE_IN_USA = 'MADE_IN_USA',
  ECO_FRIENDLY = 'ECO_FRIENDLY',
  SOCIAL_GOOD = 'SOCIAL_GOOD',
  GLUTEN_FREE = 'GLUTEN_FREE',
  ORGANIC = 'ORGANIC',
  PLANT_BASED = 'PLANT_BASED',
  VEGAN = 'VEGAN',
  KOSHER_CERTIFIED = 'KOSHER_CERTIFIED',
  WOMAN_OWNED = 'WOMAN_OWNED',
  LGBTQ_OWNED = 'LGBTQ_OWNED',
  AAPI_OWNED = 'AAPI_OWNED',
  BIPOC_OWNED = 'BIPOC_OWNED',
  BLACK_OWNED = 'BLACK_OWNED',
  LATINO_OWNED = 'LATINO_OWNED',
}

export const FULFILLMENT_TIME_OPTIONS = [
  { label: '1-24 hours', value: '1-24 hours' },
  { label: '1-2 days', value: '25-48 hours' },
  { label: '2-4 days', value: '2-4 days' },
  { label: '5-7 days', value: '5-7 days' },
  { label: '1-2 weeks', value: '1-2 weeks' },
  { label: '2+ weeks', value: '2+ weeks' },
] as const;

export type CompanyMarketplace = {
  accessType: string;
  brandCardPhoto: string;
  brandHeaderPhoto: string;
  brandProfilePhoto: string;
  primaryCategories: string[];
  secondaryCategories: string[];
  brandDescription: string;
  fulfillmentTimes: string;
  location: string;
  nsfwProducts: boolean;
  primaryContact: MarketplaceContact;
  returnPolicyURL: string;
  revSplit: number;
  secondaryContact: MarketplaceContact;
  shippingMax: number;
  shippingMin: number;
  attributes: AttributeKey[];
  yearEstablished: number;
  websiteURL: string;
  facebookURL: string;
  instagramURL: string;
  twitterURL: string;
  linkedInURL: string;
  tikTokURL: string;
  retailerAccess: string;
  approvalThreshold?: number;
  shippingCountry: string;
  shippingRates: ShippingRates;
  onboarded: boolean;
};

export type ShippingRates = {
  us: ShippingRate | null;
  international: ShippingRate | null;
};

export type ShippingRate = {
  flatRate: number;
  freeShippingThreshold: number;
  flatRateMethod: string;
  freeShippingMethod: string;
};

function sanitizeShippingRate(rate?: Partial<ShippingRate> | null): ShippingRate | null {
  if (!rate) return null;
  return {
    flatRate: rate.flatRate ?? 0,
    freeShippingThreshold: rate.freeShippingThreshold ?? 0,
    flatRateMethod: rate.flatRateMethod ?? '', // default to empty string if undefined
    freeShippingMethod: rate.freeShippingMethod ?? '', // same here
  };
}

export function sanitizeCompanyMarketplace(
  partial: Partial<CompanyMarketplace>
): CompanyMarketplace {
  return {
    accessType: partial.accessType ?? '',
    brandCardPhoto: partial.brandCardPhoto ?? '',
    brandHeaderPhoto: partial.brandHeaderPhoto ?? '',
    brandProfilePhoto: partial.brandProfilePhoto ?? '',
    primaryCategories: partial.primaryCategories ?? [],
    secondaryCategories: partial.secondaryCategories ?? [],
    brandDescription: partial.brandDescription ?? '',
    fulfillmentTimes: partial.fulfillmentTimes ?? '',
    location: partial.location ?? '',
    nsfwProducts: partial.nsfwProducts ?? false,
    primaryContact:
      partial.primaryContact ??
      ({
        /* default MarketplaceContact */
      } as MarketplaceContact),
    returnPolicyURL: partial.returnPolicyURL ?? '',
    revSplit: partial.revSplit ?? 0,
    secondaryContact:
      partial.secondaryContact ??
      ({
        /* default MarketplaceContact */
      } as MarketplaceContact),
    shippingMax: partial.shippingMax ?? 0,
    shippingMin: partial.shippingMin ?? 0,
    attributes: partial.attributes ?? [],
    yearEstablished: partial.yearEstablished ?? 0,
    websiteURL: partial.websiteURL ?? '',
    facebookURL: partial.facebookURL ?? '',
    instagramURL: partial.instagramURL ?? '',
    twitterURL: partial.twitterURL ?? '',
    linkedInURL: partial.linkedInURL ?? '',
    tikTokURL: partial.tikTokURL ?? '',
    retailerAccess: partial.retailerAccess ?? '',
    approvalThreshold: partial.approvalThreshold ?? 0,
    shippingCountry: partial.shippingCountry ?? '',
    shippingRates: {
      us: sanitizeShippingRate(partial.shippingRates?.us),
      international: sanitizeShippingRate(partial.shippingRates?.international),
    },
    onboarded: partial.onboarded ?? false,
  };
}

export type CompanyMetafieldSchemas = Record<string, Record<string, MetafieldSchema>>;

export interface Company {
  _id: string;
  legacyCompanyId: string;
  url: string;
  users: CompanyUser[];
  commerceType: CommerceType;
  name: string;
  legalName: string;
  logoId: string;
  created: string;
  updated: string;
  plan: CompanyPlan;
  flags: CompanyFlags;
  productPlatform: Platform;
  orderPlatform: Platform;
  inventoryPlatform: Platform;
  maxShipTime: number;
  maxReturnRate: number;
  webhooks: CompanyWebhook[];
  gateway?: string;
  gettingStarted: CompanyGettingStarted;
  relatedCustomer?: string;
  syncing: CompanySyncing;
  privacy: CompanyPrivacy;
  timeZone: string;
  ediData: CompanyEDIData;
  sftp: CompanySFTPSettings;
  enableOrderPayments: boolean;
  defaultBillingSettings: CompanyDefaultBillingSettings;
  domains: CompanyDomain[];
  merchandisingSettings: MerchandisingSettings;
  locations: Address[];
  easypost: CompanyEasyPostSettings;
  shippingSettings: ShippingSettings;
  returnsSettings: CompanyReturnsSettings;

  shopify: CompanyShopifySettings;
  magento2: CompanyMagento2Auth;
  woocommerce: CompanyWooCommerceAuth;
  bigCommerce: CompanyBigCommerceAuth;

  contactEmail: string;
  billingEmail?: string;
  metafieldSchemas: CompanyMetafieldSchemas;
  metafields: Metafields;
  dataExports: CompanyDataExports;

  balance?: CompanyBalance;
  stripeAccountId?: string;
  stripeCustomerId?: string;
  stripeUSCustomerId?: string;
  enableShippingLinesShopify?: boolean;
  billing: Address;
  apiKey: string;
  testKey: string;
  defaults: CompanyDefaults;
  custom: Custom[];
  terms: CompanyTerms;

  marketplaceSettings: CompanyMarketplace;
}

export interface CompanyResponse extends Company {
  productPlatformActive: boolean;
  orderPlatformActive: boolean;
  activeAuth: CompanyActiveAuth;
}
